import Head from 'next/head'
import { useState, useRef, useEffect, useMemo } from 'react'
import { withTranslation } from 'server/i18n'
import PropTypes from 'prop-types'
import { NextSeo } from 'next-seo'
import { Box } from 'reflexbox'
import ManageContext from 'contexts/ManageContext'
import getConfig from 'next/config'

import PageTitle from 'components/PageTitle'
import BoatMainImage from 'components/BoatMainImage'
import Manage from 'components/Manage'
import ServicesBlock from 'components/ServicesBlock'
import CalculatorPromo from 'components/CalculatorPromo'
import NewsletterPromo from 'components/NewsletterPromo'
import ManagePromo from 'components/ManagePromo'
import Button from 'components/Button'
import Operate from 'components/Operate'
import { getManageFields } from 'helpers/wordpress/wp-api'

import layoutTheme from 'theme/layout-theme'

import ServiceIcon from 'public/icons/service.svg'
import BuildIcon from 'public/icons/build.svg'
import InsureIcon from 'public/icons/insure.svg'
import OperateIcon from 'public/icons/operate.svg'
import SellIcon from 'public/icons/sell.svg'
import CrewIcon from 'public/icons/crew.svg'
import TravelIcon from 'public/icons/travel.svg'
import RulerIcon from 'public/icons/ruler.svg'
import YachtCareIcon from 'public/icons/yacht-care.svg'
import CarpentryIcon from 'public/icons/carpentry.svg'
import PaintWorkIcon from 'public/icons/paint-work.svg'
import UpholsteryIcon from 'public/icons/upholstery.svg'
import MarketResearchIcon from 'public/icons/market-research.svg'
import ListingIcon from 'public/icons/listing.svg'
import SalesStrategyIcon from 'public/icons/sales-strategy.svg'
import PhotoshootingIcon from 'public/icons/photoshooting.svg'
import DigitalMarketingIcon from 'public/icons/digital-marketing.svg'
import LeadManagementIcon from 'public/icons/lead-management.svg'
import ClosingAdministrationIcon from 'public/icons/closing-administration.svg'
import Build from '../../components/Build'

const { publicRuntimeConfig } = getConfig()
const { CLOUDINARY_URL, DOMAIN_ROOT } = publicRuntimeConfig

const addIdToItem = (item, index) => {
    if (typeof item.id !== 'undefined') {
        return item
    }
    return {
        ...item,
        id: index + 1
    }
}
const getContent = t => {
    const service = [
        {
            icon: <ServiceIcon />,
            text: t('service'),
            scrollTo: 'service'
        },
        {
            icon: <BuildIcon />,
            text: t('build'),
            scrollTo: 'build'
        },
        {
            icon: <InsureIcon />,
            text: t('insure'),
            scrollTo: 'insure'
        },
        {
            icon: <OperateIcon />,
            text: t('operate'),
            scrollTo: 'operate'
        },
        {
            icon: <SellIcon />,
            text: t('sell'),
            scrollTo: 'crew'
        },
        {
            icon: <CrewIcon />,
            text: t('crew'),
            scrollTo: 'crew'
        }
    ]

    const statistics = [
        {
            number: '35+',
            text: t('experienced-specialists')
        },
        {
            number: '15+',
            text: t('years-of-experience')
        },
        {
            number: '2000+',
            text: t('serviced-boats')
        }
    ]

    const services = [
        {
            icon: <TravelIcon />,
            text: t('travel-lift')
        },
        {
            icon: <RulerIcon />,
            text: t('technical-work')
        },
        {
            icon: <YachtCareIcon />,
            text: t('yacht-care')
        },
        {
            icon: <CarpentryIcon />,
            text: t('carpenrty')
        },
        {
            icon: <PaintWorkIcon />,
            text: t('paint-work')
        },
        {
            icon: <UpholsteryIcon />,
            text: t('upholstery')
        }
    ]

    const sell = [
        {
            icon: <MarketResearchIcon />,
            text: t('sell-market')
        },
        {
            icon: <ListingIcon />,
            text: t('sell-listing')
        },
        {
            icon: <SalesStrategyIcon />,
            text: t('sell-sales')
        },
        {
            icon: <PhotoshootingIcon />,
            text: t('sell-photoshooting')
        },
        {
            icon: <DigitalMarketingIcon />,
            text: t('sell-digital')
        },
        {
            icon: <LeadManagementIcon />,
            text: t('sell-lead')
        },
        {
            icon: <ClosingAdministrationIcon />,
            text: t('sell-closing')
        }
    ]

    return {
        sell: sell.map(addIdToItem),
        services: services.map(addIdToItem),
        statistics: statistics.map(addIdToItem),
        service: service.map(addIdToItem)
    }
}

const ManagePage = ({
    canonical,
    t,
    i18n,
    endTextBlock,
    introTextBox,
    sellImageBlock,
    yachtBau,
    yachtBesatzungDe,
    yachtBesatzungEn,
    yachtBetrieb,
    yachtServiceEn,
    yachtServiceDe,
    yachtVersicherungEn,
    yachtVersicherungDe
}) => {
    const [serviceOffset, setServiceOffset] = useState(0)
    const [buildOffset, setBuildOffset] = useState(0)
    const [insureOffset, setInsureOffset] = useState(0)
    const [operateOffset, setOperateOffset] = useState(0)
    const [crewOffset, setCrewOffset] = useState(0)
    const [sellOffset, setSellOffset] = useState(0)

    const serviceBlock = useRef()
    const buildBlock = useRef()
    const insureBlock = useRef()
    const operateBlock = useRef()
    const crewBlock = useRef()

    const calculateOffset = element => element.current.getBoundingClientRect().top + document.documentElement.scrollTop

    useEffect(() => {
        setTimeout(() => {
            setServiceOffset(calculateOffset(serviceBlock))
            setBuildOffset(calculateOffset(buildBlock))
            setInsureOffset(calculateOffset(insureBlock))
            setOperateOffset(calculateOffset(operateBlock))
            setCrewOffset(calculateOffset(crewBlock))
        }, 100)
    }, [serviceOffset, buildOffset, insureOffset, operateOffset, crewOffset, sellOffset])

    const content = useMemo(() => {
        return getContent(t, i18n)
    }, [i18n.language])

    const buildTabsData = [
        {
            id: 1,
            title: t('research'),
            content: i18n.language === 'en' ? yachtBau.forschungEn : yachtBau.forschungDe,
            image: `${CLOUDINARY_URL}/v1582631902/Static/bay_pk99ga.jpg`
        },
        {
            id: 2,
            title: t('shipyard'),
            content: i18n.language === 'en' ? yachtBau.werftAuswahlEn : yachtBau.werftAuswahlDe,
            image: `${CLOUDINARY_URL}/v1582631902/Static/boat-bedroom_ik3rm1.jpg`
        },
        {
            id: 3,
            title: t('construction'),
            content: i18n.language === 'en' ? yachtBau.schliffsbauEn : yachtBau.schliffsbauDe,
            image: `${CLOUDINARY_URL}/v1582628084/Static/sea-small-boat_avehrh.jpg`
        },
        {
            id: 4,
            title: t('delivery'),
            content: i18n.language === 'en' ? yachtBau.auslieferungEn : yachtBau.auslieferungDe,
            image: `${CLOUDINARY_URL}/v1582631903/Static/sell_uspinx.jpg`
        },
        {
            id: 5,
            title: t('aftersales'),
            content: i18n.language === 'en' ? yachtBau.wartungEn : yachtBau.wartungDe,
            image: `${CLOUDINARY_URL}/v1582631902/Static/buy_sbfoes.jpg`
        }
    ]

    const operatedItems = [
        {
            title: t('accordion-docking'),
            text: i18n.language === 'en' ? yachtBetrieb.dockingEn : yachtBetrieb.dockingDe
        },
        {
            title: t('accordion-itinerary'),
            text: i18n.language === 'en' ? yachtBetrieb.itineraryEn : yachtBetrieb.itineraryDe
        },
        {
            title: t('accordion-provisioning'),
            text: i18n.language === 'en' ? yachtBetrieb.provisioningEn : yachtBetrieb.provisioningDe
        },
        {
            title: t('accordion-beverage'),
            text: i18n.language === 'en' ? yachtBetrieb.beverageEn : yachtBetrieb.beverageDe
        },
        {
            title: t('accordion-spare'),
            text: i18n.language === 'en' ? yachtBetrieb.sparePartsEn : yachtBetrieb.sparePartsDe
        },
        {
            title: t('accordion-shipping'),
            text: i18n.language === 'en' ? yachtBetrieb.shippingAgentEn : yachtBetrieb.shippingAgentDe
        },
        {
            title: t('accordion-insurance'),
            text: i18n.language === 'en' ? yachtBetrieb.insuranceEn : yachtBetrieb.insuranceDe
        },
        {
            title: t('accordion-crew'),
            text: i18n.language == 'en' ? yachtBetrieb.crewManagementEn : yachtBetrieb.crewManagementDe
        },
        {
            title: t('accordion-transport'),
            text: i18n.language === 'en' ? yachtBetrieb.transportEn : yachtBetrieb.transportDe
        },
        {
            title: t('accordion-yacht'),
            text: i18n.language === 'en' ? yachtBetrieb.administrationEn : yachtBetrieb.administrationDe
        }
    ]

    const seoData = useMemo(
        () => ({
            title: `${t('meta-data:manage-title')}`,
            description: t('meta-data:manage-description'),
            canonical: t('meta-data:manage-url'),
            openGraph: {
                title: `${t('meta-data:manage-title')}`,
                description: t('meta-data:manage-description'),
                url: t('meta-data:manage-url'),
                images: [
                    {
                        url: `${CLOUDINARY_URL}/v1582628472/Static/manage-image_tk6sdl.jpg`
                    }
                ]
            },
            languageAlternates: [
                {
                    hrefLang: 'en',
                    href: `${DOMAIN_ROOT}/manage`
                },
                {
                    hrefLang: 'de',
                    href: `${DOMAIN_ROOT}/de/manage`
                }
            ]
        }),
        [i18n.language]
    )

    return (
        <div>
            <NextSeo {...seoData} />
            <Head>
                <script
                    language="javascript"
                    type="text/javascript"
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                        __html: `(function (id, f, t, ws, ms_tr_il_08, ms_tr_il_w_01) { var tr = function (cb) { var count = 0; var callback = function () { if (count == 0) { count++; if (w) { w.w(id, t, cb); } } }; var ts = document.createElement('script'); ts.src = ws; ts.type = 'text/javascript'; ts.onload = callback; ts.onreadystatechange = function () { if (this.readyState == 'complete' || this.readyState == 'loaded') { callback(); } }; var head = document.getElementsByTagName('head')[0]; head.appendChild(ts); }; if (typeof ms_tr_il_08 === 'function') { if (ms_tr_il_w_01 === null) { tr(function() { ms_tr_il_08(id, f, t); }); } else { ms_tr_il_w_01.w(id, t, function(websiteVisitedParams) { ms_tr_il_08(id, f, t, websiteVisitedParams); }, null); } } else { tr(); }})('aCM22Q9qM8KfCk90FzujKqi-N0snHDnkJp9-wW1YfsA', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/f', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/t', 'https://a78037f57059413b89ee955ac3aa54f4.svc.dynamics.com/t/w', typeof ms_tr_il_08 === "undefined" ? null : ms_tr_il_08, typeof ms_tr_il_w_01 === "undefined" ? null : ms_tr_il_w_01);`
                    }}
                />
            </Head>

            <ManageContext.Provider
                value={{
                    serviceOffset,
                    buildOffset,
                    setServiceOffset,
                    insureOffset,
                    setInsureOffset,
                    operateOffset,
                    setOperateOffset,
                    crewOffset,
                    setCrewOffset,
                    sellOffset,
                    setSellOffset
                }}>
                <Box
                    theme={layoutTheme}
                    px={40}
                    mt={[35, null, 70, 100, 150]}
                    mb={[35, null, 70, 80]}
                    variant="center1280">
                    <PageTitle
                        title={t('managing-title')}
                        subtitle={t('managing-subtitle')}
                        titleTag="h1"
                        subtitleTag="h2"
                    />
                </Box>

                <Box theme={layoutTheme} px="20px" mb={['40px', null, null, '180px']} maxWidth="1600px" mx="auto">
                    <BoatMainImage
                        image={`${CLOUDINARY_URL}/v1582628472/Static/manage-image_tk6sdl.jpg`}
                        alt="MANAGE ALT"
                    />
                </Box>

                <Manage
                    title={i18n.language === 'en' ? introTextBox.titleEn : introTextBox.titleDe}
                    text={i18n.language === 'en' ? introTextBox.textEn : introTextBox.textDe}
                    statistics={content.statistics}
                    service={content.service}
                />

                <Box
                    theme={layoutTheme}
                    px={20}
                    // mt={[0, null, null, 0]}
                    mb={[60, null, null, 120]}
                    variant="center1300">
                    <CalculatorPromo />
                </Box>

                <ServicesBlock
                    data={content.services}
                    title={t('service')}
                    subtitle={t('service-subtitle')}
                    text={i18n.language === 'en' ? yachtServiceEn : yachtServiceDe}
                    buttonText={t('service-button')}
                    buttonLink="/inquiry?type=manage"
                    offsetFunc="service"
                    ref={serviceBlock}
                />

                <Build
                    title={t('build')}
                    subtitle={i18n.language === 'en' ? yachtBau.textBoxLeftEn : yachtBau.textBoxLeftDe}
                    text={i18n.language === 'en' ? yachtBau.textBoxRightEn : yachtBau.textBoxRightDe}
                    buttonText={t('build-button')}
                    buttonLink="/inquiry?type=manage"
                    tabsData={buildTabsData}
                    ref={buildBlock}
                />

                <ManagePromo
                    image={`${CLOUDINARY_URL}/v1582632158/Static/anchor-and-diary_kualzr.jpg`}
                    title={t('insure')}
                    subtitle={t('insure-subtitle')}
                    text={i18n.language === 'en' ? yachtVersicherungEn : yachtVersicherungDe}
                    buttonText={t('insure-button')}
                    buttonLink="/inquiry?type=manage"
                    offsetFunc="insure"
                    ref={insureBlock}
                />

                <Operate
                    title={t('operate')}
                    subtitle={t('operate-subtitle')}
                    text={i18n.language === 'en' ? yachtBetrieb.introEn : yachtBetrieb.introDe}
                    operatedItems={operatedItems}
                    textBottom={t('operate-bottom-text')}
                    buttonText={t('operate-button')}
                    buttonLink="/inquiry?type=manage"
                    ref={operateBlock}
                />

                <ManagePromo
                    image={`${CLOUDINARY_URL}/v1582632237/Static/stones-on-beach_rrgfm2.jpg`}
                    title={t('crew')}
                    subtitle={t('crew-subtitle')}
                    text={i18n.language === 'en' ? yachtBesatzungEn : yachtBesatzungDe}
                    buttonText={t('crew-button')}
                    buttonLink="/inquiry?type=manage"
                    offsetFunc="crew"
                    ref={crewBlock}
                />

                <Box
                    theme={layoutTheme}
                    px={20}
                    mt={[80, null, null, 140]}
                    mb={[80, null, null, 140]}
                    variant="center1600">
                    <NewsletterPromo
                        title={i18n.language === 'en' ? sellImageBlock.titleEn : sellImageBlock.titleDe}
                        subtitle={i18n.language === 'en' ? sellImageBlock.textEn : sellImageBlock.textDe}
                        subtitleAlt={
                            i18n.language === 'en'
                                ? 'Our premium brand ads are seen by nearly 1 million prospective buyers every month.'
                                : null
                        }
                        buttonLabel={
                            i18n.language === 'en' ? sellImageBlock.buttonLabelEn : sellImageBlock.buttonLabelDe
                        }
                        link="/sell"
                        denseSubtitle
                    />
                </Box>

                <Box theme={layoutTheme} px={20} maxWidth={1000} mx="auto" mb={[80, null, null, 150, 250]}>
                    <Box theme={layoutTheme} mb={[0, 0, 0, 60]} fontSize={[24, null, null, null, 28]}>
                        {i18n.language === 'en' ? endTextBlock.textEn : endTextBlock.textDe}
                        <br />
                        <br />
                        <Button variation="tertiary" link="/contact">
                            {i18n.language === 'en' ? endTextBlock.buttonLabelEn : endTextBlock.buttonLabelDe}
                        </Button>
                    </Box>
                </Box>
            </ManageContext.Provider>
        </div>
    )
}

ManagePage.getInitialProps = async ({ req: { i18n } = {} }) => {
    const managePageAcf = await getManageFields()

    const endTextBlock = managePageAcf?.manages?.nodes[0]?.afc?.endTextBlock
    const introTextBox = managePageAcf?.manages?.nodes[0]?.afc?.introTextBox
    const sellImageBlock = managePageAcf?.manages?.nodes[0]?.afc?.sellImageBlock
    const yachtBau = managePageAcf?.manages?.nodes[0]?.afc?.yachtBau
    const yachtBesatzungDe = managePageAcf?.manages?.nodes[0]?.afc?.yachtBesatzungDe
    const yachtBesatzungEn = managePageAcf?.manages?.nodes[0]?.afc?.yachtBesatzungEn
    const yachtBetrieb = managePageAcf?.manages?.nodes[0]?.afc?.yachtBetrieb
    const yachtServiceDe = managePageAcf?.manages?.nodes[0]?.afc?.yachtServiceDe
    const yachtServiceEn = managePageAcf?.manages?.nodes[0]?.afc?.yachtServiceEn
    const yachtVersicherungDe = managePageAcf?.manages?.nodes[0]?.afc?.yachtVersicherungDe
    const yachtVersicherungEn = managePageAcf?.manages?.nodes[0]?.afc?.yachtVersicherungEn

    return {
        canonical: i18n ? i18n.getFixedT('en')('meta-data:manage-url') : undefined,
        endTextBlock,
        introTextBox,
        sellImageBlock,
        yachtBau,
        yachtBesatzungDe,
        yachtBesatzungEn,
        yachtBetrieb,
        yachtServiceEn,
        yachtServiceDe,
        yachtVersicherungEn,
        yachtVersicherungDe,
        namespacesRequired: ['manage-page', 'meta-data']
    }
}

ManagePage.propTypes = {
    canonical: PropTypes.string,
    i18n: PropTypes.oneOfType([() => null, PropTypes.object]).isRequired,
    t: PropTypes.func.isRequired,
    endTextBlock: PropTypes.shape({}),
    introTextBox: PropTypes.shape({}),
    sellImageBlock: PropTypes.shape({}),
    yachtBau: PropTypes.shape({}),
    yachtBesatzungDe: PropTypes.string,
    yachtBesatzungEn: PropTypes.string,
    yachtBetrieb: PropTypes.shape({}),
    yachtServiceEn: PropTypes.string,
    yachtServiceDe: PropTypes.string,
    yachtVersicherungEn: PropTypes.string,
    yachtVersicherungDe: PropTypes.string
}

ManagePage.defaultProps = {
    canonical: undefined,
    endTextBlock: undefined,
    introTextBox: undefined,
    sellImageBlock: undefined,
    yachtBau: undefined,
    yachtBesatzungDe: undefined,
    yachtBesatzungEn: undefined,
    yachtBetrieb: undefined,
    yachtServiceEn: undefined,
    yachtServiceDe: undefined,
    yachtVersicherungEn: undefined,
    yachtVersicherungDe: undefined
}

export default withTranslation('manage-page', 'meta-data')(ManagePage)
